import type {
  Space,
  SpaceListResponse,
  CreateSpaceRequest,
  UpdateSpaceRequest,
  SpaceMember,
  SpaceMemberListResponse,
  AddSpaceMemberRequest,
  BulkAddSpaceMembersRequest,
  BulkAddSpaceMembersResponse,
  UpdateSpaceMemberRequest,
  SpaceTemplate,
  SpaceTemplateListResponse,
  CreateTemplateFromSpaceRequest,
  AssignableUserListResponse,
  SpaceGroupListResponse,
  SpaceGroupSyncResponse,
  LinkSpaceGroupsRequest,
  UpdateSpaceGroupRequest,
  KeycloakGroupListResponse,
  KeycloakUserSearchResponse,
  SpaceUserGroup,
  SpaceUserGroupListResponse,
  CreateSpaceUserGroupRequest,
  UpdateSpaceUserGroupRequest,
} from '@lens/shared';
import { apiRequest } from '../../shared/api/client.ts';

/**
 * HTTP-обёртки над эндпоинтами `/api/spaces/*` и `/api/spaces/:id/members/*`.
 * См. docs/api-design.md §4.3-4.4. Ошибки пробрасываются как ApiError.
 */

/**
 * Тестовые пространства смоук-деплоя (slug/имя вида `smoke-<timestamp>`)
 * не должны попадать в пользовательский UI (рельс, home, «Недавнее», ⌘K).
 * Это единая точка фильтрации списка пространств — сами записи в БД не трогаем.
 */
const SMOKE_SPACE_RE = /^smoke-\d+$/i;
function isSmokeSpace(s: Space): boolean {
  return SMOKE_SPACE_RE.test(s.slug) || SMOKE_SPACE_RE.test(s.name);
}

export async function listSpaces(): Promise<SpaceListResponse> {
  const res = await apiRequest<SpaceListResponse>('/api/spaces', { method: 'GET' });
  return {
    ...res,
    mine: res.mine.filter((s) => !isSmokeSpace(s)),
    available: res.available.filter((s) => !isSmokeSpace(s)),
  };
}

export async function getSpace(id: string): Promise<Space> {
  return apiRequest<Space>(`/api/spaces/${id}`, { method: 'GET' });
}

/**
 * Резолвит короткую ссылку `/s/:slug` в uuid пространства. Дальше фронт
 * работает по uuid (вложенные ресурсы `/api/spaces/:id/...`), а адрес в
 * строке остаётся человекочитаемым. См. `/api/spaces/resolve-slug`.
 */
export async function resolveSpaceSlug(
  slug: string,
): Promise<{ id: string; slug: string; name: string }> {
  return apiRequest(`/api/spaces/resolve-slug?slug=${encodeURIComponent(slug)}`, {
    method: 'GET',
  });
}

export async function createSpace(input: CreateSpaceRequest & { templateId?: string }): Promise<Space> {
  return apiRequest<Space>('/api/spaces', { method: 'POST', body: input });
}

export async function listTemplates(): Promise<SpaceTemplateListResponse> {
  return apiRequest<SpaceTemplateListResponse>('/api/templates', { method: 'GET' });
}

export async function saveSpaceAsTemplate(
  input: CreateTemplateFromSpaceRequest,
): Promise<SpaceTemplate> {
  return apiRequest<SpaceTemplate>('/api/templates', { method: 'POST', body: input });
}

export async function updateSpace(id: string, input: UpdateSpaceRequest): Promise<Space> {
  return apiRequest<Space>(`/api/spaces/${id}`, { method: 'PATCH', body: input });
}

export async function archiveSpace(id: string): Promise<void> {
  await apiRequest<void>(`/api/spaces/${id}/archive`, { method: 'POST' });
}

export async function unarchiveSpace(id: string): Promise<void> {
  await apiRequest<void>(`/api/spaces/${id}/unarchive`, { method: 'POST' });
}

export async function deleteSpace(id: string): Promise<void> {
  await apiRequest<void>(`/api/spaces/${id}`, { method: 'DELETE' });
}

export async function listMembers(
  spaceId: string,
  search?: string,
): Promise<SpaceMemberListResponse> {
  // Без явного limit backend режет список дефолтным limit=20 → в настройках
  // отображалась только часть участников. Берём верхнюю границу схемы (500),
  // как и пикер `listAssignableUsers`.
  const params = new URLSearchParams({ limit: '500' });
  if (search) params.set('search', search);
  return apiRequest<SpaceMemberListResponse>(
    `/api/spaces/${spaceId}/members?${params.toString()}`,
    { method: 'GET' },
  );
}

/**
 * Кандидаты на добавление в участники (пикер «Добавить участников»).
 * Доступно space-админу — в отличие от `/api/admin/users`, который заперт
 * за platform-админом и раньше отдавал пикеру пустой список.
 */
export async function listAssignableUsers(
  spaceId: string,
  search?: string,
): Promise<AssignableUserListResponse> {
  const params = new URLSearchParams({ limit: '500' });
  if (search) params.set('search', search);
  return apiRequest<AssignableUserListResponse>(
    `/api/spaces/${spaceId}/assignable-users?${params.toString()}`,
    { method: 'GET' },
  );
}

export async function addMember(
  spaceId: string,
  input: AddSpaceMemberRequest,
): Promise<SpaceMember> {
  return apiRequest<SpaceMember>(`/api/spaces/${spaceId}/members`, {
    method: 'POST',
    body: input,
  });
}

export async function addMembers(
  spaceId: string,
  input: BulkAddSpaceMembersRequest,
): Promise<BulkAddSpaceMembersResponse> {
  return apiRequest<BulkAddSpaceMembersResponse>(`/api/spaces/${spaceId}/members/bulk`, {
    method: 'POST',
    body: input,
  });
}

export async function updateMemberRole(
  spaceId: string,
  userId: string,
  input: UpdateSpaceMemberRequest,
): Promise<SpaceMember> {
  return apiRequest<SpaceMember>(`/api/spaces/${spaceId}/members/${userId}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function removeMember(spaceId: string, userId: string): Promise<void> {
  await apiRequest<void>(`/api/spaces/${spaceId}/members/${userId}`, { method: 'DELETE' });
}

// ─── Доступ по группам Keycloak ──────────────────────────────────────────
//
// Привязка постоянная: состав пересчитывается при входе человека и по
// кнопке «Обновить». Любая мутация возвращает и группы, и пересчитанный
// список участников — фронт перерисовывает таблицу целиком, без догадок.

export async function listSpaceGroups(spaceId: string): Promise<SpaceGroupListResponse> {
  return apiRequest<SpaceGroupListResponse>(`/api/spaces/${spaceId}/groups`, {
    method: 'GET',
  });
}

export async function linkSpaceGroups(
  spaceId: string,
  input: LinkSpaceGroupsRequest,
): Promise<SpaceGroupSyncResponse> {
  return apiRequest<SpaceGroupSyncResponse>(`/api/spaces/${spaceId}/groups`, {
    method: 'POST',
    body: input,
  });
}

export async function updateSpaceGroupRole(
  spaceId: string,
  groupId: string,
  input: UpdateSpaceGroupRequest,
): Promise<SpaceGroupSyncResponse> {
  return apiRequest<SpaceGroupSyncResponse>(`/api/spaces/${spaceId}/groups/${groupId}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function unlinkSpaceGroup(
  spaceId: string,
  groupId: string,
): Promise<SpaceGroupSyncResponse> {
  return apiRequest<SpaceGroupSyncResponse>(`/api/spaces/${spaceId}/groups/${groupId}`, {
    method: 'DELETE',
  });
}

export async function syncSpaceGroups(spaceId: string): Promise<SpaceGroupSyncResponse> {
  return apiRequest<SpaceGroupSyncResponse>(`/api/spaces/${spaceId}/groups/sync`, {
    method: 'POST',
  });
}

// ─── Группы пользователей внутри пространства ────────────────────────────────────
//
// Набор участников под общим названием («Команда Android»), которым
// заполняют поля типа «участник». К доступу отношения не имеет — это не
// группы Keycloak выше. Список читают все участники (он нужен в поле),
// правят только админы.

export async function listSpaceUserGroups(spaceId: string): Promise<SpaceUserGroupListResponse> {
  return apiRequest<SpaceUserGroupListResponse>(`/api/spaces/${spaceId}/user-groups`, {
    method: 'GET',
  });
}

export async function createSpaceUserGroup(
  spaceId: string,
  input: CreateSpaceUserGroupRequest,
): Promise<SpaceUserGroup> {
  const res = await apiRequest<{ group: SpaceUserGroup }>(`/api/spaces/${spaceId}/user-groups`, {
    method: 'POST',
    body: input,
  });
  return res.group;
}

export async function updateSpaceUserGroup(
  spaceId: string,
  groupId: string,
  input: UpdateSpaceUserGroupRequest,
): Promise<SpaceUserGroup> {
  const res = await apiRequest<{ group: SpaceUserGroup }>(
    `/api/spaces/${spaceId}/user-groups/${groupId}`,
    { method: 'PATCH', body: input },
  );
  return res.group;
}

export async function deleteSpaceUserGroup(spaceId: string, groupId: string): Promise<void> {
  await apiRequest<void>(`/api/spaces/${spaceId}/user-groups/${groupId}`, { method: 'DELETE' });
}

/** Поиск групп Keycloak для пикера. Пустой запрос отдаёт верхушку дерева. */
export async function searchKeycloakGroups(
  spaceId: string,
  search: string,
): Promise<KeycloakGroupListResponse> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  return apiRequest<KeycloakGroupListResponse>(
    `/api/spaces/${spaceId}/keycloak/groups?${params.toString()}`,
    { method: 'GET' },
  );
}

/** Подгруппы одного уровня — пикер подгружает их при раскрытии строки. */
export async function listKeycloakGroupChildren(
  spaceId: string,
  groupId: string,
): Promise<KeycloakGroupListResponse> {
  return apiRequest<KeycloakGroupListResponse>(
    `/api/spaces/${spaceId}/keycloak/groups/${encodeURIComponent(groupId)}/children`,
    { method: 'GET' },
  );
}

/** Поиск людей в Keycloak — включая тех, кого в Lens ещё нет. */
export async function searchKeycloakUsers(
  spaceId: string,
  search: string,
): Promise<KeycloakUserSearchResponse> {
  const params = new URLSearchParams({ search });
  return apiRequest<KeycloakUserSearchResponse>(
    `/api/spaces/${spaceId}/keycloak/users?${params.toString()}`,
    { method: 'GET' },
  );
}
